import { FC } from "react";
import { Autocomplete, Chip, Stack, TextField, Typography } from "@mui/material";
import { useFleets } from "../../hooks/useFleets";
import { useLibraryAircraftOptions } from "../../hooks/useLibrary";

type AssignmentOption = {
  kind: UserAircraftAssignment["kind"];
  value: string;
  label: string;
};

type Props = {
  value: UserAircraftAssignment[];
  onChange: (next: UserAircraftAssignment[]) => void;
  disabled?: boolean;
  operatorRef?: number | null;
};

const optionKey = (option: AssignmentOption | UserAircraftAssignment) =>
  `${option.kind}:${option.value}`;

export const AircraftAssignmentPicker: FC<Props> = ({
  value,
  onChange,
  disabled = false,
  operatorRef = null,
}) => {
  const { data: aircraftOptions } = useLibraryAircraftOptions(!disabled, operatorRef);
  const { fleets } = useFleets(operatorRef, !disabled);
  const fleetRows = (fleets?.data || []) as Fleet[];

  const options: AssignmentOption[] = [
    ...(aircraftOptions?.registrations || []).map((registration: string) => ({
      kind: "registration" as const,
      value: registration,
      label: `Reg: ${registration}`,
    })),
    ...(aircraftOptions?.types || []).map((type: string) => ({
      kind: "icao_type" as const,
      value: type,
      label: `Type: ${type}`,
    })),
    ...fleetRows
      .filter((fleet: Fleet) => fleet.id != null)
      .map((fleet: Fleet) => ({
        kind: "fleet" as const,
        value: String(fleet.id),
        label: `Fleet: ${fleet.name}`,
      })),
  ];

  const selected = value
    .map((assignment) => {
      const match = options.find((option) => optionKey(option) === optionKey(assignment));
      return (
        match || {
          kind: assignment.kind,
          value: assignment.value,
          label:
            assignment.kind === "registration"
              ? `Reg: ${assignment.value}`
              : assignment.kind === "icao_type"
              ? `Type: ${assignment.value}`
              : `Fleet: ${assignment.value}`,
        }
      );
    })
    .filter(Boolean);

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        Aircraft access (optional). Leave empty so all users keep open library access until
        dedicated selections exist.
      </Typography>
      <Autocomplete
        multiple
        disabled={disabled}
        options={options}
        value={selected}
        isOptionEqualToValue={(a, b) => optionKey(a) === optionKey(b)}
        getOptionLabel={(option) => option.label}
        onChange={(_, next) =>
          onChange(next.map((entry) => ({ kind: entry.kind, value: entry.value })))
        }
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={optionKey(option)}
              label={option.label}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label="Aircraft / type / fleet" placeholder="Select…" />
        )}
      />
    </Stack>
  );
};
